/*
 * The rail's last entry: who is signed in, what is theirs, and the two ways out. Until it existed a
 * second account needed a second browser profile, because nothing in the interface ended a session
 * (S0-REPORT §1).
 *
 * The trigger is the face alone — the name, the `@handle` and the presence in words stand at the
 * top of the menu instead (VISUAL.md §7.1), because a display name under a face in a 72 px column
 * was the one caption the copy could not control. The menu then carries everything the rail stopped
 * carrying: the status group first, because a status is a fact about the person that block names and
 * this menu is the one surface in the product that is about that person; then *People & groups*,
 * because People is not a rail row (goal: "not too many tabs") and this is where it lives; Settings;
 * the keyboard sheet, on a device that has a keyboard; and, under a rule, the two ways out. Every row
 * has a glyph, the way a row in the conversation menu has.
 *
 * The status group is one door and two writes. *Set a status* opens the dialog and names what stands
 * there while something does; *Clear status* and *Show me as away* are written from the row itself,
 * because each of them is the whole gesture and a dialog to confirm a tick would be furniture. A row
 * never draws the emoji: a menu label is a plain string, and an emoji outside `.tb-emoji` is a tofu
 * box on a machine whose system font has no colour glyphs (X-A1 D12) — so the picture stands in the
 * identity block above, where this file owns the markup, and the rows carry the words.
 *
 * "Sign out" ends this session alone. "Sign out everywhere" ends every other one first — a step-up
 * route, so the server may ask for the password again and the one mounted prompt answers it — and
 * then ends this one, which is what a person means by the word: the browser they are holding is
 * somewhere too. Both leave with a full page load, exactly as signing in arrives with one: the
 * model, the socket and the databases are opened once at boot, against the session of that moment.
 * Signing out of this browser is not destructive and is not painted as though it were; the danger
 * ink is the other row's, which ends sessions on devices the person is not holding (X-A3 V24).
 *
 * A guest's identity block says *Guest account* under the handle: the rail of a guest has fewer
 * places than a colleague's, and this is where the reader learns why, in the one surface that is
 * about them.
 */

import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { Button, Menu, type MenuItem } from "../../ui/index.ts"
import { authApi, errorText, withStepUp, type ProfilePatch } from "../auth/index.ts"
import { noHoverPointer } from "../composer/pointer.ts"
import { t } from "../i18n/index.ts"
import { Avatar, isGuest, PersonFace, StatusLine, usePresence, withAway } from "../people/index.ts"
// Past the area's index, the way `core/groups` reaches this area's files: the dialog is wanted on
// this one surface and would otherwise travel into every bundle that draws an avatar.
import { clearedStatus, statusName, statusOf } from "../people/status.ts"
import { StatusDialog } from "../people/StatusDialog.tsx"
import { useModel } from "./context.tsx"
import { useLayoutMode } from "./layout.ts"
import "./shell.css"
import { useOpenShortcutSheet } from "./ShortcutProvider.tsx"

/** Where a person lands after signing out. */
const AFTER_SIGN_OUT = "/login"

export function AccountMenu() {
	const me = useModel((model) => model.me)
	// Through the same merge every other dot in the product goes through (`people/presence.ts`): this
	// is the surface the away claim is *made* on, so it is the last surface that may go on drawing
	// the transport's observation after it. A menu whose switch says one thing and whose identity
	// block two rows above says the other is a menu nobody can believe.
	const presence = withAway(usePresence(me?.id), me?.statusAway)
	const navigate = useNavigate()
	const narrow = useLayoutMode() === "narrow"
	const openSheet = useOpenShortcutSheet()
	const [failure, setFailure] = useState("")
	const [statusOpen, setStatusOpen] = useState(false)

	if (me === null) return null

	const status = statusOf(me)
	// The words a row can carry. A bare *Show me as away* has none — it is a dot, not a line — so the
	// row goes on offering to set a status rather than announcing one nobody wrote.
	const published = statusName(status)

	/**
	 * A status write from the menu itself. Both of them are one field or three and neither opens the
	 * dialog: the tick is the whole gesture, and so is taking a line down. What the menu then shows
	 * comes back through `account.updated`, which is also what moves the other tabs (protocol.md
	 * §2.6) — the rail draws the model and never a guess of its own.
	 */
	function write(patch: ProfilePatch) {
		setFailure("")
		void authApi.updateProfile(patch).catch((err: unknown) => {
			setFailure(errorText(err))
		})
	}

	async function leave(action: () => Promise<void>) {
		setFailure("")
		try {
			await action()
		} catch (err) {
			// A sign-out that did not happen must not look as though it did: the person stays where
			// they are and is told, rather than being shown a login screen over a live session.
			setFailure(errorText(err))
			return
		}
		window.location.assign(AFTER_SIGN_OUT)
	}

	const items: MenuItem[] = [
		// The status group, above the places: it is about the person the block at the top of the menu
		// names, and the rest of the menu is about where they are going.
		{
			id: "status",
			label:
				published === ""
					? t("people.status.set")
					: t("people.status.change", { status: published }),
			icon: "emoji",
			onSelect: () => setStatusOpen(true),
		},
		...(published === ""
			? []
			: [
					{
						id: "statusClear",
						label: t("people.status.clear"),
						icon: "close",
						onSelect: () => write(clearedStatus()),
					} satisfies MenuItem,
				]),
		{
			id: "statusAway",
			label: t("people.status.showAway"),
			icon: "moon",
			// A setting and not an action, so the row reports its state and `onSelect` flips it. It is
			// one field on its own: the line somebody wrote, and the moment it clears at, are none of
			// this tick's business (protocol.md §3.2).
			checked: me.statusAway === true,
			onSelect: () => write({ statusAway: me.statusAway !== true }),
		},
		{
			id: "people",
			label: t("shell.nav.people"),
			icon: "people",
			separatorBefore: true,
			onSelect: () => void navigate({ to: "/people" }),
		},
		{
			id: "settings",
			label: t("shell.nav.settings"),
			icon: "settings",
			onSelect: () => void navigate({ to: "/settings/appearance" }),
		},
		// The keyboard sheet, where there is a keyboard to read it with. On a phone the row offered a
		// thumb a table of forty-three chords it cannot press (X-B1 P9). The pointer is asked once,
		// while the menu is built: a device does not grow a keyboard with its menu open.
		...(noHoverPointer()
			? []
			: [
					{
						id: "shortcuts",
						label: t("shell.shortcuts.title"),
						icon: "keyboard",
						onSelect: openSheet,
					} satisfies MenuItem,
				]),
		{
			id: "signOut",
			label: t("auth.account.signOut"),
			icon: "logOut",
			separatorBefore: true,
			onSelect: () => void leave(() => authApi.logout()),
		},
		{
			id: "signOutEverywhere",
			label: t("auth.account.signOutEverywhere"),
			icon: "logOut",
			danger: true,
			onSelect: () =>
				void leave(async () => {
					await withStepUp(() => authApi.revokeOtherSessions())
					// A step-up mints a new session token and the synchroniser token dies with the old
					// one; the sign-out below is a mutating call and would be refused with the value
					// this tab still holds.
					await authApi.session()
					await authApi.logout()
				}),
		},
	]

	// The person, said once at the top of their own menu: the face, the name, the handle and the
	// state in words — the three lines the hover card and the profile open with as well. Their own
	// status stands under them, drawn by the one component every surface draws a status with
	// (`core/people/PersonStatus.tsx`), because this is the surface it is set from and a person must
	// be able to read what they published where they published it.
	const identity = (
		<div className="tb-account-menu__identity">
			<Avatar id={me.id} name={me.displayName} url={me.avatarUrl} size="md" {...{ presence }} />
			<div className="tb-account-menu__who">
				<p className="tb-account-menu__name">{me.displayName}</p>
				<p className="tb-account-menu__meta">
					{`@${me.handle}`}
					{presence === undefined ? null : (
						<>
							{" · "}
							{t(presence === "online" ? "people.presence.online" : "people.presence.away")}
						</>
					)}
				</p>
				{isGuest(me) ? <span className="tb-pill">{t("shell.guest.label")}</span> : null}
				<StatusLine status={status} />
			</div>
		</div>
	)

	return (
		<div className="tb-rail__account">
			<Menu
				header={identity}
				trigger={
					<Button
						className="tb-rail__account-trigger"
						aria-label={t("shell.nav.account", { name: me.displayName })}
						before={
							<>
								{/* The face wears its presence dot with the rail as the ring's ground
								    (`--tb-face-ring`); on the bar it is the smaller square under "You",
								    because a 56 px bar holds a face and a caption and nothing more. */}
								<PersonFace
									person={{
										id: me.id,
										name: me.displayName,
										avatarUrl: me.avatarUrl,
										// The claim travels with the person, the way it does in `chipPerson`:
										// the face builds its own dot, so a literal that left this out would
										// draw the reader online in the rail while every colleague sees them
										// away.
										away: me.statusAway === true,
									}}
									size={narrow ? "sm" : "md"}
									hover={false}
								/>
								{narrow ? <span className="tb-rail__label">{t("shell.nav.you")}</span> : null}
							</>
						}
					/>
				}
				items={items}
			/>
			{/* Mounted beside the menu and not inside it: the menu closes as the entry is chosen, and a
			    dialog inside the panel would leave with it. */}
			<StatusDialog open={statusOpen} onOpenChange={setStatusOpen} me={me} />
			{failure === "" ? null : (
				<p className="tb-rail__account-error" role="alert">
					{failure}
				</p>
			)}
		</div>
	)
}
